import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Table,
    Button,
    Space,
    Popconfirm,
    message,
    Input,
    Form,
    Modal,
    Typography,
    Divider
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    BuildOutlined,
    FileTextOutlined
} from '@ant-design/icons';
import { pbxService } from '../services/api';
import PBXFieldsManager from './PBXFieldsManager';

import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const PBXFormsDrawer = ({ visible, onClose }) => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const canEdit = permissions.includes('callcenter.campaigns.forms.edit');
    const [loading, setLoading] = useState(false);
    const [forms, setForms] = useState([]);
    const [search, setSearch] = useState('');

    // Form Metadata Modal
    const [metadataModalVisible, setMetadataModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [currentForm, setCurrentForm] = useState(null);
    const [form] = Form.useForm();

    // Fields Manager
    const [fieldsManagerVisible, setFieldsManagerVisible] = useState(false);
    const [selectedFormForFields, setSelectedFormForFields] = useState(null);

    useEffect(() => {
        if (visible) {
            fetchForms();
        }
    }, [visible]);

    const fetchForms = async () => {
        setLoading(true);
        try {
            const res = await pbxService.listForms(search);
            if (res.status === 'success') {
                setForms(res.data);
            }
        } catch (error) {
            message.error('Error al cargar formularios');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setModalMode('create');
        setCurrentForm(null);
        form.resetFields();
        setMetadataModalVisible(true);
    };

    const handleEditMetadata = (record) => {
        setModalMode('edit');
        setCurrentForm(record);
        form.setFieldsValue(record);
        setMetadataModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await pbxService.deleteForm(id);
            if (res.status === 'success') {
                message.success('Formulario eliminado');
                fetchForms();
            }
        } catch (error) {
            message.error('Error al eliminar formulario');
        }
    };

    const handleMetadataSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            let res;
            if (modalMode === 'create') {
                res = await pbxService.createForm(values);
            } else {
                res = await pbxService.updateForm({ ...values, id: currentForm.id });
            }

            if (res.status === 'success') {
                message.success(`Formulario ${modalMode === 'create' ? 'creado' : 'actualizado'}`);
                setMetadataModalVisible(false);
                fetchForms();
            }
        } catch (error) {
            message.error('Error al guardar metadatos');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenFieldsManager = (record) => {
        setSelectedFormForFields(record);
        setFieldsManagerVisible(true);
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Campos',
            dataIndex: 'field_count',
            key: 'field_count',
            align: 'center',
            render: (count) => <Text type="secondary">{count}</Text>
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<BuildOutlined />}
                        onClick={() => handleOpenFieldsManager(record)}
                        title="Gestionar Campos"
                    />
                    {canEdit && (
                        <>
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => handleEditMetadata(record)}
                                title="Editar Nombre/Desc"
                            />
                            <Popconfirm
                                title="¿Estás seguro de eliminar este formulario?"
                                onConfirm={() => handleDelete(record.id)}
                                okText="Sí"
                                cancelText="No"
                            >
                                <Button icon={<DeleteOutlined />} danger title="Eliminar" />
                            </Popconfirm>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <Drawer
                title={
                    <span>
                        <FileTextOutlined style={{ marginRight: 8 }} />
                        Gestión de Formularios PBX
                    </span>
                }
                width={700}
                onClose={onClose}
                open={visible}
                extra={
                    canEdit && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleCreate}
                        >
                            Nuevo Formulario
                        </Button>
                    )
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Input.Search
                        placeholder="Buscar formulario..."
                        onSearch={fetchForms}
                        onChange={(e) => setSearch(e.target.value)}
                        enterButton
                    />
                </div>

                <Table
                    dataSource={forms}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Drawer>

            {/* Modal for Name/Description */}
            <Modal
                title={modalMode === 'create' ? "Nuevo Formulario" : "Editar Metadatos"}
                open={metadataModalVisible}
                onOk={handleMetadataSubmit}
                onCancel={() => setMetadataModalVisible(false)}
                confirmLoading={loading}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Nombre del Formulario"
                        name="name"
                        rules={[{ required: true, message: 'El nombre es obligatorio' }]}
                    >
                        <Input placeholder="Ej. Encuesta Post-Llamada" />
                    </Form.Item>
                    <Form.Item
                        label="Descripción"
                        name="description"
                    >
                        <Input.TextArea rows={3} placeholder="Breve propósito del formulario..." />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Fields Manager Drawer */}
            {selectedFormForFields && (
                <PBXFieldsManager
                    visible={fieldsManagerVisible}
                    onClose={() => {
                        setFieldsManagerVisible(false);
                        fetchForms(); // Refresh field count
                    }}
                    formInfo={selectedFormForFields}
                />
            )}
        </>
    );
};

export default PBXFormsDrawer;
